import { API_BASE } from './config/api.config.js';
import { FEATURES } from './features';
import './styles/card-animations.css';
import React, { useState, useMemo, useEffect, useRef, useCallback, lazy, Suspense } from "react";
  import { Routes, Route, useNavigate, useLocation } from "react-router-dom";
  import { ShoppingCart, X, Plus, Minus, Trash2, ChevronDown, Package, CreditCard, Truck, User, LogOut, Shield, Loader2, Boxes } from "lucide-react";
  import { authAPI } from "./services/api";
  import { useProducts } from "./hooks/useProducts";
  import ErrorBoundary from "./components/ErrorBoundary";
import ChunkErrorBoundary, { clearChunkReloadAttempt } from "./components/ChunkErrorBoundary";
// ERA-001: Era filter support
import { ERA_CONFIGS } from "./components/shared/EraBadge";
// Phase 5: Lazy load admin to reduce initial bundle size
const AdminLayout = lazy(() => import("./components/admin/AdminLayout"));
// C4.P4: Lazy load WelcomePage for public QR scan landing
const WelcomePage = lazy(() => import("./components/public/WelcomePage"));
// LAUNCH:C7.P2 -- Lazy load MaestroHomepage for public visitors
const MaestroHomepage = lazy(() => import("./components/maestro/MaestroHomepage"));
// MDM-MOBILE-P1-001: Lazy load grading screens for mobile-first grading flow
import GradingFlowProvider from './hooks/GradingFlowProvider';
// V-NAV-DEVICE R1: the ONE vault entry point. App owns `isVaultOpen`, so it passes the module
// its own overlay-opener; every other shell gets the ?vault=open signal from the same function.
import { openVault as openVaultShared, vaultHref } from './utils/vaultNav';
// R3's route guard asks the same classifier the vault entry point does.
import { isMobileDevice } from './utils/deviceClass';
// V-NAV-ACCOUNT: ONE account dropdown, rendered on every authed surface.
import AccountMenu from './components/maestro/AccountMenu';
// V-NAV-ROUTE / V-NAV-SIGNEDOUT: the bare /maestro/vault redirect and the signed-out vault
// guard. A second react-router-dom import on purpose: the header import line above is
// edited by a sibling branch (storefront auth nav), so this stays out of that hunk.
import { Navigate } from "react-router-dom";
const GradeHome = lazy(() => import("./components/maestro/grading/GradeHome"));
const GradingUploadCapture = lazy(() => import("./components/maestro/grading/GradingUploadCapture"));
const GradingProcessing = lazy(() => import("./components/maestro/grading/GradingProcessing"));
const GradeResults = lazy(() => import("./components/maestro/grading/GradeResults"));
const GradeHistory = lazy(() => import("./components/maestro/grading/GradeHistory"));
const CompSalesDetail = lazy(() => import("./components/maestro/grading/CompSalesDetail"));
const GradeFeedback = lazy(() => import("./components/maestro/grading/GradeFeedback"));
// MDM-MOBILE-P2-001: Lazy load defect vision screens
const DefectOverlay = lazy(() => import("./components/maestro/defect/DefectOverlay"));
const DefectSummary = lazy(() => import("./components/maestro/defect/DefectSummary"));
const PressCandidateView = lazy(() => import("./components/maestro/defect/PressCandidateView"));
// MDM-MOBILE-P3-001: Lazy load convention intelligence screens
const ConDashboard = lazy(() => import("./components/maestro/convention/ConDashboard"));
const ConDetail = lazy(() => import("./components/maestro/convention/ConDetail"));
const MoneyMoves = lazy(() => import("./components/maestro/convention/MoneyMoves"));
const CreatorSchedule = lazy(() => import("./components/maestro/convention/CreatorSchedule"));
const ConCheckin = lazy(() => import("./components/maestro/convention/ConCheckin"));
// MDM-MOBILE-P4B-001: Lazy load vault mobile screens
const VaultHome = lazy(() => import("./components/maestro/vault/VaultHome"));
const CollectionList = lazy(() => import("./components/maestro/vault/CollectionList"));
const VaultItemDetail = lazy(() => import("./components/maestro/vault/ItemDetail"));
// MDM-MOBILE-P4C/D-001: Lazy load vault quick-add, hunt list, smart sets
const QuickAdd = lazy(() => import("./components/maestro/vault/QuickAdd"));
const HuntList = lazy(() => import("./components/maestro/vault/HuntList"));
const SmartSetsMobile = lazy(() => import("./components/maestro/vault/SmartSetsMobile"));
// V-NAV-DEVICE RULE 3: what a non-phone sees if it reaches a phone-only vault route.
// LAUNCH:C8.P3.2: Lazy load DemoPage for standalone /demo route
const DemoPage = lazy(() => import("./components/maestro/demo/DemoPage"));
// BR-AUTH-SIGNIN-NO-PASSWORD-RESET-001 PHASE_C: standalone /reset-password landing
const PasswordResetConfirm = lazy(() => import("./components/auth/PasswordResetConfirm"));
// LAUNCH:C9.P2.3: Lazy load Supply Lab pages for public catalog browse
const SupplyLab = lazy(() => import("./components/maestro/supply-lab/SupplyLab"));
const BundlesPage = lazy(() => import("./components/bundles/BundlesPage"));
const BundleDetailPage = lazy(() => import("./components/bundles/BundleDetailPage"));
const SupplyLabProduct = lazy(() => import("./components/maestro/supply-lab/SupplyLabProduct"));
// ⛔⛔ `const OrderComplete = lazy(...)` AND `goToStore` BOTH LIVED HERE AND BOTH ARE
// GONE. This branch and #452 built the same page independently, so the merge produced TWO
// identical lazy() declarations of the same name -- App.jsx did not parse at all
// ("The symbol \"OrderComplete\" has already been declared"), and nothing said so until
// the bundler ran. Development's declaration, with its G33 comment, is kept below.
//
// `goToStore` went with it: it was this branch's /order-complete exit handler
// (window.location.href = "/"), and both route declarations now carry development's
// handlers instead -- the signed-out one a full navigation, the signed-in one an
// IN-ROUTER navigate() that is mutation-proved to actually leave the page. An exported
// function with zero callers is an uncovered function in a file the gate holds at 100%,
// which is the exact metric #452 was merged to fix.
// MAESTRO:P0D: Lazy load chat interface (auth-required)
const MaestroChat = lazy(() => import("./components/maestro/chat/MaestroChat"));
// MAESTRO:P1D — Admin conversation review panel
const MaestroAdminPanel = lazy(() => import("./components/maestro/admin/MaestroAdminPanel"));

// CARD:P4 — Public card page
const PublicCardPage = lazy(() => import("./components/collector-card/PublicCardPage"));

// BRD-PRELAUNCH-ARTIFACTS-001 PHASE_A2 A-05 + A-06: Routed legal pages.
// Lazy-loaded so they don't bloat the main bundle. Pair with Footer.jsx (A-07).
const LegalTerms = lazy(() => import("./components/legal/pages/Terms"));
const LegalPrivacy = lazy(() => import("./components/legal/pages/Privacy"));
const LegalCookies = lazy(() => import("./components/legal/pages/Cookies"));
const LegalAUP = lazy(() => import("./components/legal/pages/AUP"));
const LegalDMCA = lazy(() => import("./components/legal/pages/DMCA"));
const LegalDpa = lazy(() => import("./components/legal/pages/Dpa"));
// G33: the page a 3DS buyer returns to. Its path existed only as a string in
// CheckoutForm's return_url and no route declared it.
const OrderComplete = lazy(() => import("./components/OrderComplete"));
import Footer from "./components/Footer";
  import CheckoutForm, { OrderSuccess } from "./components/CheckoutForm";
  import ComingSoon from "./components/ComingSoon";
  import AuthModal from "./components/AuthModal";
import AboutContact from "./components/AboutContact";
import ConventionQuickAccess from "./components/ConventionQuickAccess";
import ConventionModeBanner from "./components/ConventionModeBanner";
import ConventionRedeemPage from "./components/ConventionRedeemPage";
import ConventionsPage from "./components/ConventionsPage";
import MyOrders from "./components/MyOrders";
import RefundPolicy from "./components/RefundPolicy";
import AccountDashboard from "./components/AccountDashboard";
import VerifyEmail from "./components/VerifyEmail";
import UnsubscribePage from "./components/email/UnsubscribePage";
// STORY-LEGAL-001: Privacy and GDPR compliance components
import PrivacyPolicy from "./components/PrivacyPolicy";
import GDPR from "./components/GDPR";
// USER-CON-001: The Vault and Gamification.
// V-NAV-EGRESS: ConventionDiscovery + ConPrepChecklist overlay mounts were REMOVED here. Their
// only trigger was an `onOpenConventionDiscovery` prop passed to VaultPage, which VaultPage
// never accepted -- the overlays were unreachable in every build that shipped. The components
// themselves stay (own suites) for whoever wires a real entry point.
import VaultPage from "./components/vault/VaultPage";
import GamificationDashboard from "./components/gamification/GamificationDashboard";
// IMPL-20260106-OCR-GRADER-PHASE3: AI Grading moved to admin CreateComicsForm

// IDENTITY:P1 — Alias Claim Banner (lazy-loaded, renders conditionally)
const AliasClaimBanner = lazy(() => import("./components/identity/AliasClaimBanner"));
// IDENTITY:P3.B — IdentityProvider wraps main app content
import { IdentityProvider } from './contexts/IdentityContext';
// IMPL-AUTH-SIGNIN-SILENT-REJECTION-001 (R-1) — route-agnostic toast provider
import { useNotification } from './contexts/NotificationProvider';
// LAUNCH:C9.P3.R — cart state lifted to CartContext so routed Supply Lab pages share it
import { useCart } from './contexts/CartContext';
// SL-IMG-1 — un-imaged supplies in the cart render the branded placeholder, never a broken <img>
import SupplyImagePlaceholder from './components/maestro/supply-lab/SupplyImagePlaceholder';

// FE-PERF-003: CSS animations moved to separate file for caching
import './styles/animations.css';

  // ============================================================================
  // BUILD INFO - Update these on each release
  // FE-STATE-002: buildDate now injected at build time via vite.config.js
  // ============================================================================
  const BUILD_INFO = {
    version: "1.6.0",
    buildNumber: 16,
    buildDate: __BUILD_DATE__,
    environment: __BUILD_ENV__
  };

  // P3-12: UNDER_CONSTRUCTION flag is now fetched from API via /api/config
  // This allows toggling via environment variable without rebuilding frontend

  // ============================================================================
  // MAIN APP COMPONENT
  // ============================================================================
  // NOTE: AuthModal has been extracted to components/AuthModal.jsx to prevent
  // form state resets on parent re-renders (FE-002 fix)
  export default function App() {
    // FE-004: Products from API instead of static array
    // FE-PERF-006: Added server-side pagination support
    const {
      products,
      loading: productsLoading,
      error: productsError,
      pagination,
      goToPage,
      changePerPage
    } = useProducts({ initialPerPage: 100 }); // Load more initially for homepage sections


    // State management
    // LAUNCH:C9.P3.R — cart state lives in CartContext (shared with Supply Lab)
    const { cart, addToCart, updateQuantity, removeFromCart, clearCart, cartTotal, cartCount } = useCart();
    // G33. In-router navigation for the order-complete "continue shopping" exit. App is
    // ALWAYS mounted under a Router -- BrowserRouter at main.jsx:89, MemoryRouter in all
    // five test files that render it -- so this hook cannot throw here.
    const navigate = useNavigate();
    // BETA-COMMERCE-290: used to gate the Vault landing to the app root only (see the
    // vault-open effect below) so a deep-link/refresh on a real route is not hijacked.
    const location = useLocation();

    const [isCartOpen, setIsCartOpen] = useState(false);
    const [currentView, setCurrentView] = useState("shop");
    const [completedOrder, setCompletedOrder] = useState(null);
    // ⭐ S11c. The SERVER's money breakdown, held only after create-payment-intent has
    // returned it. `null` means "not quoted yet" and the summary says exactly that rather
    // than inventing a shipping rate to fill the row.
    const [checkoutQuote, setCheckoutQuote] = useState(null);

    // ⛔ A QUOTE IS ONLY VALID FOR THE CART IT WAS QUOTED FOR. Changing the cart after a
    // quote arrives -- adding a bin to a bag order crosses a tier boundary -- would leave
    // a stale total on screen that no longer matches what the next intent will charge.
    // Discarding it puts the summary back to "Calculated at checkout", which is true.
    useEffect(() => {
      setCheckoutQuote(null);
    }, [cart]);

    const { showNotification } = useNotification();

    // Auth state
    // P1-5: Removed localStorage token storage - now using HttpOnly cookies
    const [user, setUser] = useState(null);
    const [isAuthModalOpen, setIsAuthModalOpen] = useState(false);
    const [isAdminOpen, setIsAdminOpen] = useState(false);
    const [isAboutContactOpen, setIsAboutContactOpen] = useState(false);
    const [isMyOrdersOpen, setIsMyOrdersOpen] = useState(false);
    const [isRefundPolicyOpen, setIsRefundPolicyOpen] = useState(false);
    const [isConventionsOpen, setIsConventionsOpen] = useState(false);
    const [isAccountOpen, setIsAccountOpen] = useState(false);
    // STORY-LEGAL-001: Privacy and GDPR modal state
    const [isPrivacyPolicyOpen, setIsPrivacyPolicyOpen] = useState(false);
    const [isGDPROpen, setIsGDPROpen] = useState(false);
    // USER-CON-001: Vault and Gamification state (V-NAV-EGRESS: the Convention Discovery / Con
    // Prep state that followed was dead -- see the VaultPage import note)
    const [isVaultOpen, setIsVaultOpen] = useState(false);
    // SL-NAV-SWEEP (1) is now AccountMenu's job: the store shell's open/close state, its Escape
    // handler and its click-outside listener moved into the shared component, which every surface
    // renders. Keeping a second copy here would register document listeners for a menu this file
    // no longer draws.
    const [isGamificationOpen, setIsGamificationOpen] = useState(false);

    // V-NAV-VIEWPORT / V-NAV-DEVICE: ONE opener for every "go to the Vault" site (the landing
    // effect below, the user-menu item, the order-complete exit, both logos). The desktop
    // overlay (VaultPage: three panes, keyboard nav, command palette) is the wrong surface on a
    // phone -- the routed mobile screens exist for exactly that.
    //
    // ⛔ THE TEST WAS `useBreakpoint().isMobile`, i.e. window.innerWidth < 768. A DESKTOP user who
    // narrowed their window was handed the phone UI mid-session, and widening it again flipped
    // them back: two different products, decided by a drag of the window edge. The device does
    // not change while the page is open; the window does. `isMobileDevice()` asks about the
    // DEVICE (UA Client Hints first, UA string only as a fallback) and anything not confidently
    // a phone gets the desktop vault.
    const openVault = useCallback(
      () => openVaultShared({ navigate, openDesktopOverlay: () => setIsVaultOpen(true) }),
      [navigate],
    );

    // BETA-COMMERCE-290: an authenticated user's LANDING is the Vault (where the majority of
    // activity happens). Open it once on the user null->set transition (an explicit login or a
    // session-restore on app entry) -- but ONLY at the app root "/". A refresh or deep-link on a
    // real route (/maestro/grade, /maestro/supply-lab, /reset-password, /order-complete, or any
    // path that still renders the store shell) MUST land THERE, not be yanked to the Vault. The
    // `location.pathname === "/"` gate is exactly what scopes the auto-open to the landing rather
    // than to auth state. Navigating home later (logo click) does NOT re-open it -- only the
    // transition does -- so it never fights in-app navigation.
    const prevUserRef = useRef(user);
    useEffect(() => {
      const wasSignedOut = !prevUserRef.current;
      prevUserRef.current = user;
      if (wasSignedOut && user && location.pathname === "/") {
        openVault();
      }
    }, [user, location.pathname, openVault]);

    // V-NAV-DEVICE RULE 1: the desktop vault is STATE (isVaultOpen), not a route, so a plain
    // anchor in another shell (MaestroNav's wordmark, which lives outside this component tree)
    // cannot raise it. It links to "/?vault=open" instead and this effect turns that arrival
    // into the overlay -- the one place the signal is consumed, for every logo in the app.
    //
    // The parameter is stripped with `replace` so it never enters history: a Back press must
    // not re-open the vault, and a copied URL should not carry a one-shot instruction.
    useEffect(() => {
      if (!user) return;
      const params = new URLSearchParams(location.search);
      const vault = params.get("vault") === "open";
      const panel = params.get("panel");
      // V-NAV-ACCOUNT: My Account / Rewards / My Orders are storeApp MODALS, not routes, and
      // storeApp is not mounted on the MaestroNav surfaces (homepage, Supply Lab). They arrive
      // as the same one-shot flag the desktop vault uses, and are consumed here.
      const PANELS = { account: setIsAccountOpen, rewards: setIsGamificationOpen, orders: setIsMyOrdersOpen };
      const openPanel = panel ? PANELS[panel] : undefined;
      if (!vault && !openPanel) return;
      params.delete("vault");
      params.delete("panel");
      const rest = params.toString();
      navigate({ pathname: location.pathname, search: rest ? `?${rest}` : "" }, { replace: true });
      if (vault) openVault();
      if (openPanel) openPanel(true);
    }, [user, location.search, location.pathname, navigate, openVault]);

    const [authMode, setAuthMode] = useState("login");
    const [authLoading, setAuthLoading] = useState(true);

    // T-011a: Email verification URL state
    const [verifyEmailToken, setVerifyEmailToken] = useState(null);

    // EMAIL-021: Unsubscribe URL state
    const [unsubscribeToken, setUnsubscribeToken] = useState(null);

    // P3-12: Under construction flag from API (defaults to true for safety)
    // GATE0-HOMEPAGE-FLAG-ROUTING-DECOUPLE-001 (OQ-1/OQ-2): INTENTIONALLY RETAINED as the
    // reserved beta-access signal even though routing no longer reads it — the signed-out
    // homepage is auth-decided (see the !user branch below). Do NOT delete under a future
    // no-unused-vars re-enable/cleanup pass; the beachhead/beta workstream owns wiring it.
    const [underConstruction, setUnderConstruction] = useState(true);

    // C4.P4: Welcome page state (public QR scan landing)
    const [isWelcomePage, setIsWelcomePage] = useState(false);

    // LAUNCH:C8.P3.2: Demo page state (public, no auth required)
    const [isDemoPage, setIsDemoPage] = useState(false);

    // CARD:P4: Public card page state (public, no auth required)
    const [isCardPage, setIsCardPage] = useState(false);

    // MAESTRO:CONACCESS P4: Convention QR redemption page state (public, no auth required)
    const [isConventionRedeemPage, setIsConventionRedeemPage] = useState(false);

    // T-011a: Check URL for email verification on mount
    useEffect(() => {
      const path = window.location.pathname;
      const params = new URLSearchParams(window.location.search);

      if (path === '/verify-email' && params.get('token')) {
        setVerifyEmailToken(params.get('token'));
      }

      // EMAIL-021: Unsubscribe URL routing
      if (path.startsWith('/email/unsubscribe/')) {
        const pathParts = path.split('/');
        const token = pathParts[3]; // /email/unsubscribe/{token}
        if (token) {
          setUnsubscribeToken(token);
          window.history.replaceState({}, '', '/');
        }
      }

      // C4.P4: Welcome page routing (public, no auth required)
      /* c8 ignore start */
      if (path === '/welcome') {
        setIsWelcomePage(true);
      }
      /* c8 ignore stop */

      // LAUNCH:C8.P3.2: Demo page routing (public, no auth required)
      if (path === '/demo') {
        setIsDemoPage(true);
      }

      // CARD:P4: Public card page routing (public, no auth required)
      /* v8 ignore next 3 -- URL routing guard, tested via E2E */
      if (path.startsWith('/card/')) {
        setIsCardPage(true);
      }

      // MAESTRO:CONACCESS P4: Convention QR redemption routing (public, no auth required)
      /* v8 ignore next 3 -- URL routing guard, tested via E2E */
      if (path.startsWith('/c/')) {
        setIsConventionRedeemPage(true);
      }
    }, []);

    // MDM-SENTRY-CHUNK-ERROR-20260118: Clear chunk reload marker on successful app load
    useEffect(() => {
      clearChunkReloadAttempt();
    }, []);

    // P1-5: Load user from cookie-based session on mount
    // No longer needs localStorage - server reads token from HttpOnly cookie
    useEffect(() => {
      setAuthLoading(true);
      authAPI.me()
        .then(userData => {
          setUser(userData);
        })
        .catch(() => {
          // Not authenticated or session expired
          setUser(null);
        })
        .finally(() => {
          setAuthLoading(false);
        });
    }, []);

    // P3-12: Fetch config from API on mount
    // Note: VITE_API_BASE_URL already includes /api suffix (e.g., https://api.maestrograding.ai/api)
    useEffect(() => {
      // API_BASE imported from config
      fetch(`${API_BASE}/config`)
        .then(res => res.json())
        .then(config => {
          setUnderConstruction(config.under_construction ?? true);
        })
        .catch(() => {
          // On error, default to under construction for safety
          setUnderConstruction(true);
        });
    }, []);

    // LAUNCH:C9.P3.R — cart persistence + operations moved to CartContext.

    // LAUNCH:C9.P3.R — cartTotal/cartCount come from CartContext.
    // Category/product detail views removed with the store-era substrate
    // (unreachable: nothing set currentView to "category" after the homepage
    // sections were excised).

    // Auth functions
    // P1-5: Updated for cookie-based auth - no more localStorage
    // USER-CON-001: Enhanced registration with extra profile fields
    const handleSignup = async (name, email, password, extraFields = {}) => {
      try {
        const result = await authAPI.register(name, email, password, extraFields);
        // P1-5: Server sets HttpOnly cookies automatically
        // Fetch user data to confirm auth worked
        const userData = await authAPI.me();
        setUser(userData);
        setIsAuthModalOpen(false);
        const firstName = extraFields.first_name || name.split(' ')[0];
        showNotification(`Welcome to MAESTRO, ${firstName}!`);
        return true;
      } catch (err) {
        showNotification(err.message || "Registration failed", "error");
        return false;
      }
    };

    const handleLogin = async (email, password) => {
      try {
        const result = await authAPI.login(email, password);
        // P1-5: Server sets HttpOnly cookies automatically
        // Fetch user data to confirm auth worked
        const userData = await authAPI.me();
        setUser(userData);
        setIsAuthModalOpen(false);
        showNotification(`Welcome back, ${userData.name}!`);
        return true;
      } catch (err) {
        showNotification("Invalid email or password", "error");
        return false;
      }
    };

    const handleLogout = async () => {
      try {
        // P1-5: Call logout endpoint to clear HttpOnly cookies
        await authAPI.logout();
      } catch (err) {
        // Logout even if API call fails
        console.error("Logout API error:", err);
      }
      setUser(null);
      setIsAdminOpen(false);
      showNotification("You've been logged out");
    };



    // ============================================================================
    // RENDER
    // ============================================================================

    // CARD:P4: Show PublicCardPage for /card/:alias route (public, no auth)
    /* v8 ignore next 17 -- standalone card page early-return, tested via E2E */
    if (isCardPage) {
      return (
        <ChunkErrorBoundary>
          <Suspense fallback={
            <div className="min-h-screen bg-maestro-bg flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
            </div>
          }>
            <Routes>
              <Route path="/card/:alias" element={<PublicCardPage />} />
              <Route path="*" element={<PublicCardPage />} />
            </Routes>
          </Suspense>
        </ChunkErrorBoundary>
      );
    }

    // MAESTRO:CONACCESS P4: Show ConventionRedeemPage for /c/:token route (public, no auth)
    /* v8 ignore next 3 -- standalone redemption page early-return, tested via E2E */
    if (isConventionRedeemPage) {
      return <ConventionRedeemPage />;
    }

    // LAUNCH:C8.P3.2: Show DemoPage for /demo route (public, no auth)
    if (isDemoPage) {
      return (
        <ChunkErrorBoundary>
          <Suspense fallback={
            <div className="min-h-screen bg-[#0A0A0F] flex items-center justify-center">
              <Loader2 className="w-8 h-8 text-maestro-gold animate-spin" />
            </div>
          }>
            <DemoPage />
          </Suspense>
        </ChunkErrorBoundary>
      );
    }

    // C4.P4: Show WelcomePage for /welcome route (public, no auth)
    /* c8 ignore start */
    if (isWelcomePage) {
      return (
        <Suspense fallback={
          <div className="min-h-screen bg-zinc-950 flex items-center justify-center">
            <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
          </div>
        }>
          <WelcomePage />
        </Suspense>
      );
    }
    /* c8 ignore stop */

    // T-011a: Show email verification page if token present
    if (verifyEmailToken) {
      return (
        <VerifyEmail
          token={verifyEmailToken}
          onComplete={() => {
            setVerifyEmailToken(null);
            // Refresh user in case email verification grants access
            authAPI.me()
              .then(userData => setUser(userData))
              .catch(() => setUser(null));
          }}
        />
      );
    }

    // EMAIL-021: Show unsubscribe page if token present
    if (unsubscribeToken) {
      return (
        <UnsubscribePage
          token={unsubscribeToken}
          onComplete={() => setUnsubscribeToken(null)}
        />
      );
    }

    // LAUNCH:C7.P2 -- MaestroHomepage replaces ComingSoon for public visitors
    // MDM-MOBILE-P1-001: Grading routes rendered alongside MaestroHomepage
    // GATE0-HOMEPAGE-FLAG-ROUTING-DECOUPLE-001: auth decides the homepage — signed-out
    // visitors ALWAYS get MaestroHomepage regardless of UNDER_CONSTRUCTION; the store/app
    // renders only for authenticated users. The flag is reserved for beta-access gating
    // (OQ-1) and MUST NOT re-enter this condition.
    if (!user) {
      // V-NAV-SIGNEDOUT: every vault API answers 401 without a session, so the six vault
      // screens in this tree were declared for nobody -- a signed-out deep-link rendered a
      // screen whose every fetch failed. They now resolve to the homepage instead.
      //
      // ⛔ NOT WHILE THE SESSION IS STILL BEING READ. This tree also renders during the
      // `authAPI.me()` window on a refresh (user is null until it resolves). A bare
      // <Navigate> there would bounce a SIGNED-IN user's /maestro/vault/collection refresh to
      // "/" before their session came back -- and the landing effect would then open the Vault
      // at the wrong place. Hold a blank frame until auth settles; redirect only a real no-session.
      const vaultSignedOutGuard = authLoading
        ? <div className="h-screen bg-[#0A0A0F]" data-testid="vault-auth-pending" />
        : <Navigate to="/" replace />;
      return (
        <IdentityProvider user={user}>
        <ChunkErrorBoundary>
          <Suspense fallback={<div className="h-screen bg-[#0A0A0F]" />}>
            {/* ⛔ BETA-COMMERCE-298 (P0-1): ABOVE <Routes>, not inside an element. Upload, processing and results are three ROUTES; a provider per element would be three instances again. */}
            <GradingFlowProvider>
            <Routes>
              <Route path="/maestro/grade" element={<GradeHome />} />
              <Route path="/maestro/grade/upload" element={<GradingUploadCapture />} />
              <Route path="/maestro/grade/processing" element={<GradingProcessing />} />
              <Route path="/maestro/grade/results/:id" element={<GradeResults />} />
              <Route path="/maestro/grade/history" element={<GradeHistory />} />
              <Route path="/maestro/grade/comps/:id" element={<CompSalesDetail />} />
              <Route path="/maestro/grade/feedback" element={<GradeFeedback />} />
              {/* MDM-MOBILE-P2-001: Defect vision screens */}
              <Route path="/maestro/defect/overlay/:gradeRequestId" element={<DefectOverlay />} />
              <Route path="/maestro/defect/summary/:gradeRequestId" element={<DefectSummary />} />
              <Route path="/maestro/defect/press/:gradeRequestId" element={<PressCandidateView />} />
              {/* MDM-MOBILE-P3-001: Convention intelligence screens */}
              <Route path="/maestro/convention/dashboard" element={<ConDashboard />} />
              <Route path="/maestro/convention/detail/:conventionId" element={<ConDetail />} />
              <Route path="/maestro/convention/money-moves/:conventionId" element={<MoneyMoves />} />
              <Route path="/maestro/convention/creators/:conventionId" element={<CreatorSchedule />} />
              <Route path="/maestro/convention/checkin/:conventionId" element={<ConCheckin />} />
              {/* MDM-MOBILE-P4B-001 / P4C/D-001: the vault paths, signed OUT. Declared so the
                  URLs stay stable (the guard above decides what a no-session visitor sees). */}
              {/* V-NAV-ROUTE: the bare path had no route in either tree (fell to the catch-all). */}
              <Route path="/maestro/vault" element={<Navigate to="/maestro/vault/home" replace />} />
              <Route path="/maestro/vault/home" element={vaultSignedOutGuard} />
              <Route path="/maestro/vault/collection" element={vaultSignedOutGuard} />
              <Route path="/maestro/vault/item/:itemId" element={vaultSignedOutGuard} />
              <Route path="/maestro/vault/add" element={vaultSignedOutGuard} />
              <Route path="/maestro/vault/hunt-list" element={vaultSignedOutGuard} />
              <Route path="/maestro/vault/smart-sets" element={vaultSignedOutGuard} />
              {/* LAUNCH:C9.P2.3: Supply Lab catalog routes */}
              {/* LAUNCH:C9.P3.R — real onLogin handlers (were dead clicks) so supply visitors can start sign-in */}
              {/* BUNDLES_ENABLED already existed in features.js and defaulted on; the flag
                  was there, the route was not, so the whole bundle surface was unreachable. */}
              {FEATURES.BUNDLES_ENABLED && <Route path="/maestro/bundles" element={<BundlesPage />} />}
              {/* The browse page navigated here and NOTHING was mounted -- every card
                  click fell through to the catch-all. */}
              {FEATURES.BUNDLES_ENABLED && <Route path="/maestro/bundles/:slug" element={<BundleDetailPage />} />}
              {FEATURES.SUPPLIES_ENABLED && <Route path="/maestro/supply-lab" element={<SupplyLab onLogin={() => { setAuthMode('login'); setIsAuthModalOpen(true); }} />} />}
              {FEATURES.SUPPLIES_ENABLED && <Route path="/maestro/supply-lab/:mdmSku" element={<SupplyLabProduct onLogin={() => { setAuthMode('login'); setIsAuthModalOpen(true); }} />} />}
              {/* BRD-PRELAUNCH-ARTIFACTS-001 PHASE_A2 A-06: 6 routed legal pages (no auth required) */}
              <Route path="/legal/terms" element={<LegalTerms />} />
              <Route path="/legal/privacy" element={<LegalPrivacy />} />
              <Route path="/legal/cookies" element={<LegalCookies />} />
              <Route path="/legal/aup" element={<LegalAUP />} />
              <Route path="/legal/dmca" element={<LegalDMCA />} />
              <Route path="/legal/dpa" element={<LegalDpa />} />
              {/* CARD:P4: Public card page (no auth required) */}
              <Route path="/card/:alias" element={<PublicCardPage />} />
              {/* MAESTRO:P0D: Chat interface — auth required, user is always falsy in this block */}
              {/* IMPL-AUTH-SIGNIN-DEADCLICK-001: onLogin here is the pre-auth modal trigger, NOT a post-auth session refresh. Wires the nav Sign In button to open AuthModal below. */}
              <Route path="/maestro/chat" element={<MaestroHomepage onLogin={() => { setAuthMode('login'); setIsAuthModalOpen(true); }} />} />
              {/* BR-AUTH-SIGNIN-NO-PASSWORD-RESET-001 PHASE_C (BR-GAP-005): /reset-password BEFORE the catch-all so email-link arrivals resolve to PasswordResetConfirm. */}
              <Route path="/reset-password" element={<PasswordResetConfirm />} />

              {/* G33. Verified before choosing: signed-out falls to the "*" below
                  (MaestroHomepage); authenticated falls to the store splat at the bottom
                  of this file. Declared in BOTH, because this page needs no session. */}
              <Route path="/order-complete" element={<OrderComplete onContinue={() => { window.location.assign("/"); }} />} />
              {/* IMPL-AUTH-SIGNIN-DEADCLICK-001: onLogin here is the pre-auth modal trigger, NOT a post-auth session refresh. Wires the nav Sign In button to open AuthModal below. */}
              {/* A redirect can return before the session is re-read. Served in BOTH trees
                  so the buyer never lands on the marketing homepage after paying. */}
              <Route path="*" element={<MaestroHomepage onLogin={() => { setAuthMode('login'); setIsAuthModalOpen(true); }} />} />
            </Routes>
            </GradingFlowProvider>
          </Suspense>
        </ChunkErrorBoundary>
        {/* IMPL-AUTH-SIGNIN-DEADCLICK-001: AuthModal mount for the unauthenticated landing. Mirrors the authenticated-block render at App.jsx:1155 so the homepage nav Sign In button has a modal target. */}
        {isAuthModalOpen && (
          <AuthModal
            mode={authMode}
            setMode={setAuthMode}
            onClose={() => setIsAuthModalOpen(false)}
            onLogin={handleLogin}
            onSignup={handleSignup}
          />
        )}
        {/* BRD-PRELAUNCH-ARTIFACTS-001 PHASE_A2 A-07: top-level Footer mount (legal nav, every route) */}
        <Footer />
        </IdentityProvider>
      );
    }

    // LAUNCH:C9.P3.R (R4) — the store SPA tree is hoisted so a minimal Routes
    // wrapper can serve /maestro/supply-lab to signed-in users while every
    // other path renders the store unchanged.
    // V-NAV-ACCOUNT: built ONCE (declared HERE, below handleLogout -- a `const` referenced
    // above its declaration is in the temporal dead zone and throws at render, not at build) and handed to every surface, so the item set and the
    // and the destinations cannot drift apart between the store header, the vault overlay and MaestroNav.
    // storeApp owns these modals, which is why the node is constructed here rather than in each
    // consumer.
    // No `user ? ... : null` guard: AccountMenu already renders nothing without a user (and that
    // is tested), so a ternary here only adds a branch nothing can reach -- storeApp renders for
    // authenticated users only. An unreachable branch is an un-provable one.
    const accountMenu = (
      <AccountMenu
        user={user}
        onAccount={() => setIsAccountOpen(true)}
        onVault={openVault}
        onSupplyLab={FEATURES.SUPPLIES_ENABLED ? () => navigate("/maestro/supply-lab") : undefined}
        onRewards={() => setIsGamificationOpen(true)}
        onOrders={() => setIsMyOrdersOpen(true)}
        onSignOut={handleLogout}
      />
    );

    const storeApp = (
      <div data-testid="store-shell" className={`min-h-screen bg-zinc-950 text-zinc-100 ${isAdminOpen ? 'h-screen overflow-hidden' : ''}`} style={{ fontFamily: "'Barlow', sans-serif" }}>
        {/* IMPL-AUTH-SIGNIN-SILENT-REJECTION-001 (R-1): toast hoisted to NotificationProvider in main.jsx */}

        {/* IDENTITY:P1 — Alias Claim Banner for con-harvested users */}
        {user && (
          <Suspense fallback={null}>
            <AliasClaimBanner />
          </Suspense>
        )}

        {/* Header */}
        <header className="sticky top-0 z-[45] bg-zinc-950/95 backdrop-blur-md border-b border-zinc-800">
          <div className="max-w-7xl mx-auto px-3 sm:px-4 py-3 sm:py-4">
            <div className="flex items-center justify-between gap-2">
              {/* Logo -- V-NAV-DEVICE RULE 1: EVERY logo instance goes to the user's vault, the
                  same one MaestroNav's wordmark reaches. This one used to call
                  setCurrentView("shop"), which left a signed-in user on the empty shop hub (the
                  SL-NAV-E defect).
                  An <a> rather than a <div>: the logo is a navigation control, so it must be
                  focusable, announced as a link, and middle-clickable.

                  ⛔ NO `user ? ... : ...` GUARD HERE, for the same reason `accountMenu` above
                  carries none: the `if (!user)` branch at the top of this component RETURNS its
                  own tree, so storeApp is only ever built for an authenticated user. A signed-out
                  arm on this anchor is a branch nothing can reach -- and an unreachable branch is
                  an un-provable one, which is exactly what the coverage gate caught it as. */}
              <a
                href={vaultHref()}
                aria-label="Open your Vault"
                onClick={(e) => {
                  // The ONE real branch: a modified click (or a middle/right button) must keep the
                  // browser's native open-in-new-tab behaviour instead of being swallowed by the
                  // overlay -- the reason the logo is an <a> and not a <div> in the first place.
                  if (e.metaKey || e.ctrlKey || e.shiftKey || e.button !== 0) return;
                  e.preventDefault();
                  openVault();
                }}
                className="flex items-center gap-2 sm:gap-3 cursor-pointer flex-shrink-0 min-w-0 focus:outline-none focus:ring-2 focus:ring-maestro-gold focus:ring-offset-2 focus:ring-offset-zinc-950 rounded-xl no-underline"
              >
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-zinc-800 border border-maestro-gold/30 rounded-xl flex items-center justify-center shadow-lg shadow-maestro-gold/10 flex-shrink-0">
                  <span className="font-maestro-mono text-xl sm:text-2xl font-bold text-maestro-gold">M</span>
                </div>
                <div className="min-w-0">
                  <h1 className="font-maestro-mono text-xl sm:text-2xl font-bold truncate"
                      style={{ letterSpacing: '0.15em' }}><span className="text-maestro-gold">M</span><span className="text-white">AESTRO</span></h1>
                  <p className="hidden sm:block text-xs text-zinc-500 -mt-1 truncate">
                    Collector Intelligence Platform
                  </p>
                </div>
              </a>

              {/* SL-NAV-B: the authenticated shell had NO path to the Supply Lab (the beta
                  shop) -- FEATURES was imported but only gated the routes. Mirrors the
                  MaestroNav link; navigates in-router (the route is declared in this tree),
                  href kept for a11y / open-in-new-tab. */}
              {FEATURES.SUPPLIES_ENABLED && (
                <a
                  href="/maestro/supply-lab"
                  onClick={(e) => {
                    e.preventDefault();
                    navigate("/maestro/supply-lab");
                  }}
                  className={`font-maestro-body text-sm transition-colors min-h-touch flex items-center px-1 focus:outline-none focus:ring-2 focus:ring-maestro-gold rounded ${
                    window.location.pathname.startsWith("/maestro/supply-lab")
                      ? "text-maestro-gold"
                      : "text-zinc-300 hover:text-maestro-gold"
                  }`}
                >
                  Supply Lab
                </a>
              )}

              {/* User & Cart Buttons */}
              <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                {/* User Button — GATE0-HOMEPAGE-FLAG-ROUTING-DECOUPLE-001: the store renders
                    only for authenticated users, so the signed-out Sign In leg was removed
                    (sign-in lives on the MaestroHomepage gate + its AuthModal mount). */}
                {/* Admin Console Button - Only for admins */}
                {user?.is_admin && (
                  <button
                    onClick={() => setIsAdminOpen(true)}
                    className="relative p-2 sm:p-2.5 bg-zinc-900 border border-red-800 rounded-lg sm:rounded-xl hover:border-red-500 transition-colors group flex-shrink-0"
                    title="Admin Console"
                  >
                    <Shield className="w-5 h-5 sm:w-6 sm:h-6 text-red-400 group-hover:text-red-500 transition-colors" />
                  </button>
                )}

                {/* Cart Button */}
                <button
                  onClick={() => setIsCartOpen(true)}
                  aria-label="Open cart"
                  className="relative p-2 sm:p-2.5 bg-zinc-900 border border-zinc-800 rounded-lg sm:rounded-xl hover:border-orange-500 transition-colors group flex-shrink-0"
                >
                  <ShoppingCart className="w-5 h-5 sm:w-6 sm:h-6 text-zinc-400 group-hover:text-orange-500 transition-colors" />
                  {cartCount > 0 && (
                    <span className="absolute -top-1.5 -right-1.5 sm:-top-2 sm:-right-2 w-5 h-5 sm:w-6 sm:h-6 bg-orange-500 rounded-full flex items-center justify-center text-[10px] sm:text-xs font-bold text-white pulse-glow">
                      {cartCount}
                    </span>
                  )}
                </button>
                {/* V-NAV-ACCOUNT: the account menu is the VERY LAST control in the bar, matching
                    MaestroNav and the vault toolbar. It used to sit BEFORE Admin Console and the
                    cart, so the one control every surface shares was in a different place on each. */}
                {accountMenu}
              </div>
            </div>

          </div>
        </header>

        {/* MAESTRO:CONACCESS P3 — Homepage banner when Convention Mode is active. */}
        <ConventionModeBanner />

        {/* Main Content */}
        {/* BETA-COMMERCE-290: the dead "SHOP THE RACK!" storefront hero (currentView === "shop")
            was retired. This app sells only supplies, which have their own /maestro/supply-lab
            pages; comics are catalog-not-commerce. The authenticated landing is now the Vault
            (see the null->set vault-open effect). The shell (header/nav/cart/Vault opener) and the
            ConventionQuickAccess widget below are kept. */}

        {/* Checkout View */}
        {currentView === "checkout" && (
          <main className="max-w-4xl mx-auto px-4 py-12">
            {completedOrder ? (
              <OrderSuccess
                order={completedOrder}
                onClose={() => {
                  setCompletedOrder(null);
                  clearCart();
                  setCurrentView("shop");
                }}
              />
            ) : (
              <>
                <button
                  onClick={() => setCurrentView("shop")}
                  className="text-orange-500 hover:text-orange-400 mb-8 flex items-center gap-2"
                >
                  ← Back to Shop
                </button>

                <h2 className="font-comic text-4xl text-white mb-8">CHECKOUT</h2>

                {/* GATE0-HOMEPAGE-FLAG-ROUTING-DECOUPLE-001: checkout is authenticated-only
                    (store behind login) — the signed-out sign-in prompt leg was removed. */}
                {cart.length === 0 ? (
                  <div className="bg-zinc-900 rounded-2xl p-8 border border-zinc-800 text-center">
                    <p className="text-zinc-400 mb-4">Your cart is empty</p>
                    <button
                      onClick={() => setCurrentView("shop")}
                      className="px-6 py-3 bg-orange-500 text-white rounded-xl font-semibold hover:bg-orange-600"
                    >
                      Continue Shopping
                    </button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 gap-8">
                    {/* Order Summary */}
                    <div className="bg-zinc-900 rounded-2xl p-6 border border-zinc-800">
                      <h3 className="font-bold text-xl text-white mb-4">Order Summary</h3>
                      <div className="space-y-4 mb-6">
                        {cart.map((item) => (
                          <div key={item.id} className="flex items-center gap-4">
                            {item.image ? (
                              <img src={item.image} alt={item.name} className="w-16 h-16 object-cover rounded-lg" />
                            ) : (
                              <SupplyImagePlaceholder variant="cart" name={item.name} category={item.category} className="w-16 h-16 flex-none rounded-lg" />
                            )}
                            <div className="flex-1">
                              <p className="text-white font-semibold">{item.name}</p>
                              <p className="text-zinc-500 text-sm">Qty: {item.quantity}</p>
                            </div>
                            <p className="text-orange-500 font-bold">${(item.price * item.quantity).toFixed(2)}</p>
                          </div>
                        ))}
                      </div>
                      {/*
                        ⛔⛔ S11c. THE THREE LINES THAT USED TO BE HERE DISPLAYED AN
                        INVENTED POLICY. `cartTotal >= 50 ? "FREE" : "$5.99"` was copied
                        from orders.py -- a route this app never calls -- and the 8% tax
                        that lived beside it there was dropped on the way. The documented
                        policy is $49/$6.99 AND IT IS PER TIER, so the button was wrong
                        about the threshold, wrong about the rate, and wrong about tax,
                        while Stripe charged a fourth number.

                        ⭐ NOTHING IS COMPUTED HERE NOW. Shipping is a server fact and it
                        arrives with the PaymentIntent; until the address is submitted
                        there is no quote, and the honest thing to show is that there is
                        no quote yet -- not a number this file made up.
                      */}
                      <div className="border-t border-zinc-800 pt-4 space-y-2">
                        <div className="flex justify-between text-zinc-400">
                          <span>Subtotal</span>
                          <span>${((checkoutQuote ? checkoutQuote.subtotal_cents / 100 : cartTotal)).toFixed(2)}</span>
                        </div>
                        <div className="flex justify-between text-zinc-400">
                          <span>Shipping</span>
                          <span>
                            {!checkoutQuote
                              ? "Calculated at checkout"
                              : checkoutQuote.shipping_cents === 0
                                ? "FREE"
                                : `$${(checkoutQuote.shipping_cents / 100).toFixed(2)}`}
                          </span>
                        </div>
                        {checkoutQuote && checkoutQuote.tax_cents > 0 && (
                          <div className="flex justify-between text-zinc-400">
                            <span>Tax</span>
                            <span>${(checkoutQuote.tax_cents / 100).toFixed(2)}</span>
                          </div>
                        )}
                        <div className="flex justify-between text-xl font-bold text-white pt-2 border-t border-zinc-800">
                          <span>Total</span>
                          <span>
                            {checkoutQuote
                              ? `$${(checkoutQuote.total_cents / 100).toFixed(2)}`
                              : "—"}
                          </span>
                        </div>
                      </div>
                    </div>

                    {/* Payment Form */}
                    <div>
                      <CheckoutForm
                        cartItems={cart}
                        onQuote={setCheckoutQuote}
                        onSuccess={(order) => {
                          setCompletedOrder(order);
                          // ⛔ P0 — THE CART MUST BE CLEARED ON SUCCESS, NOT ONLY IN
                          // OrderSuccess's onClose. clearCart() previously had exactly ONE
                          // call site in the app: the "Continue Shopping" button. A buyer
                          // who paid, saw "Order Confirmed!", and CLOSED THE TAB instead of
                          // clicking it came back to a cart still holding what they had just
                          // bought — and checking out again produced a SECOND charge, a
                          // second order and a second shipment.
                          // Safe here: both `cart.length === 0` branches live inside the
                          // `completedOrder ? ... : ...` ELSE, so an empty cart cannot
                          // preempt the confirmation screen.
                          clearCart();
                          showNotification("Order placed successfully!", "success");
                        }}
                        onCancel={() => setCurrentView("shop")}
                      />
                    </div>
                  </div>
                )}
              </>
            )}
          </main>
        )}

        {/* Cart Sidebar */}
        {isCartOpen && (
          <div className="fixed inset-0 z-50">
            {/* Overlay */}
            <div
              className="absolute inset-0 bg-black/60 backdrop-blur-sm"
              onClick={() => setIsCartOpen(false)}
            />

            {/* Cart Panel */}
            <div className="absolute right-0 top-0 h-full w-full max-w-md bg-zinc-950 border-l border-zinc-800 slide-in">
              <div className="flex flex-col h-full">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b border-zinc-800">
                  <h3 className="font-comic text-2xl text-white">YOUR CART</h3>
                  <button
                    onClick={() => setIsCartOpen(false)}
                    aria-label="Close cart"
                    className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
                  >
                    <X className="w-6 h-6 text-zinc-400" />
                  </button>
                </div>

                {/* Cart Items */}
                <div className="flex-1 overflow-auto p-6">
                  {cart.length === 0 ? (
                    <div className="text-center py-12">
                      <ShoppingCart className="w-16 h-16 text-zinc-700 mx-auto mb-4" />
                      <p className="text-zinc-500">Your cart is empty</p>
                      <button
                        onClick={() => setIsCartOpen(false)}
                        className="mt-4 text-orange-500 hover:text-orange-400 font-semibold"
                      >
                        Continue Shopping
                      </button>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {cart.map((item) => (
                        <div
                          key={item.id}
                          className="flex gap-4 bg-zinc-900 rounded-xl p-4 border border-zinc-800"
                        >
                          {item.image ? (
                            <img
                              src={item.image}
                              alt={item.name}
                              className="w-20 h-24 object-cover rounded-lg"
                            />
                          ) : (
                            <SupplyImagePlaceholder variant="cart" name={item.name} category={item.category} className="w-20 h-24 flex-none rounded-lg" />
                          )}
                          <div className="flex-1">
                            <h4 className="font-semibold text-white">{item.name}</h4>
                            {((item.source || '').toLowerCase() === 'bcw' || (item.category || '').toLowerCase() === 'supplies') && (
                              <span className="inline-flex items-center gap-1 text-xs text-blue-400 mt-0.5">
                                <Truck className="w-3 h-3" />
                                Ships from supplier
                              </span>
                            )}
                            <p className="text-orange-500 font-bold">${item.price}</p>
                            <div className="flex items-center gap-3 mt-2">
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                aria-label="Decrease quantity"
                                className="p-1 bg-zinc-800 rounded hover:bg-zinc-700 transition-colors"
                              >
                                <Minus className="w-4 h-4 text-zinc-400" />
                              </button>
                              <span className="text-white font-semibold">{item.quantity}</span>
                              <button
                                onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                aria-label="Increase quantity"
                                className="p-1 bg-zinc-800 rounded hover:bg-zinc-700 transition-colors"
                              >
                                <Plus className="w-4 h-4 text-zinc-400" />
                              </button>
                              <button
                                onClick={() => removeFromCart(item.id)}
                                aria-label="Remove from cart"
                                className="ml-auto p-1 text-red-500 hover:bg-red-500/10 rounded transition-colors"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Footer */}
                {cart.length > 0 && (
                  <div className="p-6 border-t border-zinc-800">
                    <div className="flex justify-between mb-4">
                      <span className="text-zinc-400">Subtotal</span>
                      <span className="text-2xl font-bold text-white">${cartTotal.toFixed(2)}</span>
                    </div>
                    <button
                      onClick={() => { setIsCartOpen(false); setCurrentView("checkout"); }}
                      className="w-full py-4 bg-orange-500 rounded-xl font-bold text-white hover:shadow-lg hover:shadow-orange-500/25 transition-all active:scale-[0.98]"
                    >
                      Proceed to Checkout
                    </button>
                    <p className="text-zinc-500 text-xs text-center mt-3">
                      Free shipping on orders over $50
                    </p>
                    {cart.some(item => (item.source || '').toLowerCase() === 'bcw' || (item.category || '').toLowerCase() === 'supplies') && (
                      <p className="text-blue-400/70 text-xs text-center mt-1">
                        BCW supply shipping calculated at checkout
                      </p>
                    )}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Admin Console - Phase 3: Full-page layout with ChunkErrorBoundary + ErrorBoundary */}
        {/* MDM-SENTRY-CHUNK-ERROR-20260118: ChunkErrorBoundary wraps outside to catch chunk load failures */}
        {isAdminOpen && (
          <ChunkErrorBoundary>
            <ErrorBoundary
              fallback={
                <div className="fixed inset-0 z-50 bg-zinc-950 flex flex-col items-center justify-center gap-4">
                  <p className="text-red-400">Failed to load admin console</p>
                  <button
                    onClick={() => setIsAdminOpen(false)}
                    className="px-4 py-2 bg-orange-500 text-white rounded-lg hover:bg-orange-600"
                  >
                    Close
                  </button>
                </div>
              }
            >
              <Suspense fallback={
                <div className="fixed inset-0 z-50 bg-zinc-950 flex items-center justify-center">
                  <Loader2 className="w-8 h-8 text-orange-500 animate-spin" />
                </div>
              }>
                <AdminLayout onClose={() => setIsAdminOpen(false)} />
              </Suspense>
            </ErrorBoundary>
          </ChunkErrorBoundary>
        )}

        {/* GATE0-HOMEPAGE-FLAG-ROUTING-DECOUPLE-001: the store's own AuthModal mount was
            removed — with the signed-out store legs gone it had no opener left in storeApp.
            Live mounts: the gate branch (signed-out flows) and supplyRouteAuthModal
            (signed-in supply routes). */}

        {/* About & Contact Modal - IMPL-001 */}
        {isAboutContactOpen && (
          <AboutContact onClose={() => setIsAboutContactOpen(false)} />
        )}

        {/* My Orders Modal - BCW Refund Module v1.0.0 */}
        {isMyOrdersOpen && (
          <MyOrders onClose={() => setIsMyOrdersOpen(false)} />
        )}

        {/* Refund Policy Modal - BCW Refund Module v1.0.0 */}
        {isRefundPolicyOpen && (
          <RefundPolicy onClose={() => setIsRefundPolicyOpen(false)} />
        )}

        {/* Account Dashboard - v1.6.0 */}
        {isAccountOpen && (
          <AccountDashboard
            user={user}
            onClose={() => setIsAccountOpen(false)}
            onLogout={handleLogout}
          />
        )}

        {/* STORY-LEGAL-001: Privacy Policy Modal */}
        {isPrivacyPolicyOpen && (
          <PrivacyPolicy onClose={() => setIsPrivacyPolicyOpen(false)} />
        )}

        {/* STORY-LEGAL-001: GDPR/CCPA Rights Modal */}
        {isGDPROpen && (
          <GDPR onClose={() => setIsGDPROpen(false)} />
        )}


        {/* Conventions Page - Full page view */}
        {isConventionsOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto">
            <ConventionsPage onBack={() => setIsConventionsOpen(false)} />
          </div>
        )}

        {/* USER-CON-001: The Vault - Full page view (BETA-COMMERCE-290: the authenticated landing).
            V-NAV-EGRESS: the Vault IS home, so VaultPage no longer renders a back arrow; onBack is
            accepted (deprecated, unused) only so this mount keeps compiling. Egress from the
            overlay is the sidebar's Supply Lab rail; the store user-menu underneath it is not
            reachable while the overlay is up. The dead onOpenConventionDiscovery pass-through
            (VaultPage never accepted it) is gone. */}
        {isVaultOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-zinc-950" data-testid="vault-landing">
            <VaultPage onBack={() => setIsVaultOpen(false)} accountMenu={accountMenu} />
          </div>
        )}

        {/* USER-CON-001: Gamification Dashboard - Full page view */}
        {isGamificationOpen && (
          <div className="fixed inset-0 z-50 overflow-y-auto bg-zinc-950">
            <GamificationDashboard onBack={() => setIsGamificationOpen(false)} />
          </div>
        )}

        {/* V-NAV-EGRESS: the Convention Discovery and Con Prep Checklist overlays that stood here
            were reachable ONLY through the dead onOpenConventionDiscovery pass-through above
            (see the import note at the top of this file). Removed with it. */}

        {/* Convention Quick Access - Above Shipping Banner */}
        {currentView === "shop" && (
          <ConventionQuickAccess onViewAll={() => setIsConventionsOpen(true)} />
        )}

        {/* Shipping Banner - Fixed above footer */}
        <section className="border-t border-zinc-800 bg-zinc-900/50">
          <div className="max-w-5xl mx-auto px-4 py-8">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-0 sm:divide-x divide-zinc-800">
              <div className="flex flex-col items-center text-center px-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-zinc-800 rounded-full flex items-center justify-center mb-2">
                  <Truck className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" />
                </div>
                <h4 className="font-bold text-white text-sm">Fast Shipping</h4>
                <p className="text-zinc-500 text-xs">Free on orders $50+</p>
              </div>
              <div className="flex flex-col items-center text-center px-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-zinc-800 rounded-full flex items-center justify-center mb-2">
                  <Package className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" />
                </div>
                <h4 className="font-bold text-white text-sm">Secure Packaging</h4>
                <p className="text-zinc-500 text-xs">Protective cases included</p>
              </div>
              <div className="flex flex-col items-center text-center px-4">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-zinc-800 rounded-full flex items-center justify-center mb-2">
                  <CreditCard className="w-5 h-5 sm:w-6 sm:h-6 text-orange-500" />
                </div>
                <h4 className="font-bold text-white text-sm">Secure Payment</h4>
                <p className="text-zinc-500 text-xs">PayPal, Stripe & more</p>
              </div>
            </div>
          </div>
        </section>

        {/* Footer */}
        <footer className="bg-zinc-900 border-t border-zinc-800">
          <div className="max-w-7xl mx-auto px-4 py-8">
            <div className="flex flex-col items-center gap-4">
              {/* Main Links */}
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-zinc-500 text-sm">
                <button onClick={() => setIsAboutContactOpen(true)} className="hover:text-orange-500 transition-colors">About</button>
                <button onClick={() => setIsAboutContactOpen(true)} className="hover:text-orange-500 transition-colors">Contact</button>
                <button onClick={() => setIsRefundPolicyOpen(true)} className="hover:text-orange-500 transition-colors">Returns</button>

              </div>

              {/* STORY-LEGAL-001: Legal Links */}
              <div className="flex flex-wrap justify-center gap-4 sm:gap-6 text-zinc-600 text-xs">
                <button onClick={() => setIsPrivacyPolicyOpen(true)} className="hover:text-orange-500 transition-colors">Privacy Policy</button>
                <button onClick={() => setIsGDPROpen(true)} className="hover:text-orange-500 transition-colors">Data Rights (GDPR/CCPA)</button>
              </div>

              {/* Build Info */}
              <div className="text-xs text-zinc-600">
                <p>v{BUILD_INFO.version}.{BUILD_INFO.buildNumber} • Built {new Date(BUILD_INFO.buildDate).toLocaleDateString()}</p>
              </div>
            </div>

            <div className="mt-8 pt-6 border-t border-zinc-800 text-center text-zinc-600 text-sm">
              <p>© {new Date().getFullYear()} Muñoz Digital Media, LLC. All rights reserved.</p>
            </div>
          </div>
        </footer>
      </div>
    );

    // The supply routes render OUTSIDE storeApp, so they need their own
    // AuthModal mount (storeApp's mount is unreachable from these routes —
    // this also serves signed-out visitors when under_construction is false).
    const supplyRouteAuthModal = isAuthModalOpen && (
      <AuthModal
        mode={authMode}
        setMode={setAuthMode}
        onClose={() => setIsAuthModalOpen(false)}
        onLogin={handleLogin}
        onSignup={handleSignup}
      />
    );

    // A1: the grading routes must exist for AUTHENTICATED users too.
    //
    // They were declared ONLY inside the `if (!user)` branch above, and storeApp
    // (which serves every authenticated path) contains ZERO grade routes. So a
    // signed-in user hitting /maestro/grade fell through the `path="*"` catch-all
    // into the store -- the grader simply vanished the moment anyone logged in.
    // Grading is the whole beta, and buying supplies requires signing in, so the
    // two surfaces collided exactly where a tester would meet them.
    //
    // Every one of these screens is lazy() and this branch has NO Suspense ancestor --
    // storeApp's own boundary is INSIDE storeApp and does not cover siblings.
    // Wrapping the whole block would change storeApp's error handling, so the
    // boundary goes per-route instead: additive for the new paths, and storeApp
    // renders exactly as it did before.
    const lazyRoute = (element) => (
      <ChunkErrorBoundary>
        <Suspense fallback={<div className="h-screen bg-[#0A0A0F]" />}>{element}</Suspense>
      </ChunkErrorBoundary>
    );

    // D10: a phone-only route renders its phone screen on a phone, and REDIRECTS anything else
    // to "/". It used to render MobileOnlyNotice -- a dead end: a signed-in desktop user who
    // refreshed, bookmarked or was linked to /maestro/vault/* landed on a page whose only content
    // was an explanation, with the real desktop vault one click away behind a button. The desktop
    // vault is App's `isVaultOpen` OVERLAY on "/" (see utils/vaultNav.js), not a route, so "/" IS
    // the desktop vault's address. Sending them there resolves the URL to somewhere that works
    // instead of parking them on an apology.
    //
    // NOTE: `replace` so the dead URL does not sit in history for the back button to return to.
    // NOTE: the redirect stays INSIDE lazyRoute. AppSignedInRoutesWrapped.test.jsx asserts that
    // mobileVaultRoute is defined in terms of lazyRoute, because this block has no Suspense
    // ancestor and every lazy() screen under it needs its own boundary. Wrapping a <Navigate>
    // costs nothing -- there is no chunk to suspend on -- and keeping the whole helper behind
    // lazyRoute means the guard cannot be satisfied by a wrapper that quietly drops the boundary.
    const mobileVaultRoute = (element) =>
      lazyRoute(isMobileDevice() ? element : <Navigate to="/" replace />);

    return (
      <IdentityProvider user={user}>
        {/* ⛔ BETA-COMMERCE-298 (P0-1): ABOVE <Routes>, not inside an element. Upload, processing and results are three ROUTES; a provider per element would be three instances again. */}
        <GradingFlowProvider>
        <Routes>
          <Route path="/maestro/grade" element={lazyRoute(<GradeHome />)} />
          <Route path="/maestro/grade/upload" element={lazyRoute(<GradingUploadCapture />)} />
          <Route path="/maestro/grade/processing" element={lazyRoute(<GradingProcessing />)} />
          <Route path="/maestro/grade/results/:id" element={lazyRoute(<GradeResults />)} />
          <Route path="/maestro/grade/history" element={lazyRoute(<GradeHistory />)} />
          <Route path="/maestro/grade/comps/:id" element={lazyRoute(<CompSalesDetail />)} />
          <Route path="/maestro/grade/feedback" element={lazyRoute(<GradeFeedback />)} />
          {/* The same asymmetry, for the other four families. The signed-out tree
              declares 33 routes; before the grade hoist the signed-in tree declared
              THREE, so vault, convention, defect and legal all vanished on sign-in.
              ⛔ /legal/* is the sharpest: a signed-in user could not reach terms,
              privacy, DMCA or the DPA at all. */}
          {/* V-NAV-ROUTE: bare /maestro/vault -> the mobile home (was the catch-all = store shell). */}
          {/* ⛔⛔ V-NAV-DEVICE RULE 3. These seven routes rendered the PHONE screens to whoever
              asked for them -- a typed URL, a stale bookmark, a deep link, or the wordmark. Gating
              at the ROUTE rather than at each trigger is what makes it airtight: every present and
              future way of reaching these paths is covered by one check, and a trigger added later
              cannot reintroduce the leak. A non-phone gets the notice (with a control into the
              desktop vault), never the phone UI. */}
          {/* D10: DEVICE-BRANCHED. Sending a desktop user to /maestro/vault/home just to have
              mobileVaultRoute bounce them to "/" would be two redirects and a flash of the phone
              route in history. The bare path answers each device with its own vault directly. */}
          <Route path="/maestro/vault" element={lazyRoute(isMobileDevice() ? <Navigate to="/maestro/vault/home" replace /> : <Navigate to="/" replace />)} />
          <Route path="/maestro/vault/home" element={mobileVaultRoute(<VaultHome />)} />
          <Route path="/maestro/vault/collection" element={mobileVaultRoute(<CollectionList />)} />
          <Route path="/maestro/vault/item/:itemId" element={mobileVaultRoute(<VaultItemDetail />)} />
          <Route path="/maestro/vault/add" element={mobileVaultRoute(<QuickAdd />)} />
          <Route path="/maestro/vault/hunt-list" element={mobileVaultRoute(<HuntList />)} />
          <Route path="/maestro/vault/smart-sets" element={mobileVaultRoute(<SmartSetsMobile />)} />
          <Route path="/maestro/convention/dashboard" element={lazyRoute(<ConDashboard />)} />
          <Route path="/maestro/convention/detail/:conventionId" element={lazyRoute(<ConDetail />)} />
          <Route path="/maestro/convention/money-moves/:conventionId" element={lazyRoute(<MoneyMoves />)} />
          <Route path="/maestro/convention/creators/:conventionId" element={lazyRoute(<CreatorSchedule />)} />
          <Route path="/maestro/convention/checkin/:conventionId" element={lazyRoute(<ConCheckin />)} />
          <Route path="/maestro/defect/overlay/:gradeRequestId" element={lazyRoute(<DefectOverlay />)} />
          <Route path="/maestro/defect/summary/:gradeRequestId" element={lazyRoute(<DefectSummary />)} />
          <Route path="/maestro/defect/press/:gradeRequestId" element={lazyRoute(<PressCandidateView />)} />
          <Route path="/legal/terms" element={lazyRoute(<LegalTerms />)} />
          <Route path="/legal/privacy" element={lazyRoute(<LegalPrivacy />)} />
          <Route path="/legal/cookies" element={lazyRoute(<LegalCookies />)} />
          <Route path="/legal/aup" element={lazyRoute(<LegalAUP />)} />
          <Route path="/legal/dmca" element={lazyRoute(<LegalDMCA />)} />
          <Route path="/legal/dpa" element={lazyRoute(<LegalDpa />)} />
          {/* ⛔ THESE TWO WERE THE ONLY LAZY ROUTES IN THIS BLOCK LEFT UNWRAPPED, and they
              are the beta shop. SupplyLab and SupplyLabProduct are both lazy() (:50, :51),
              this branch has NO Suspense ancestor -- see the comment above lazyRoute -- and
              the signed-OUT copies at the other Routes block DO sit inside a
              ChunkErrorBoundary + Suspense. So a SIGNED-IN visitor was the only one exposed:
              the chunk suspends with no boundary to catch it and the app white-screens, on
              the exact screens a beta tester opens to buy something.

              They were skipped when the other 27 routes were converted because they carry an
              extra child ({supplyRouteAuthModal}) and did not look like the others.
              lazyRoute takes an ELEMENT, so the fragment goes in whole. */}
          {FEATURES.SUPPLIES_ENABLED && <Route path="/maestro/supply-lab" element={lazyRoute(<>
            <SupplyLab user={user} onLogin={() => { setAuthMode('login'); setIsAuthModalOpen(true); }} onLogout={handleLogout} />
            {supplyRouteAuthModal}
          </>)} />}
          {FEATURES.SUPPLIES_ENABLED && <Route path="/maestro/supply-lab/:mdmSku" element={lazyRoute(<>
            <SupplyLabProduct user={user} onLogin={() => { setAuthMode('login'); setIsAuthModalOpen(true); }} onLogout={handleLogout} />
            {supplyRouteAuthModal}
          </>)} />}
          {/* ⛔ MUST PRECEDE THE CATCH-ALL. Below it, /order-complete renders storeApp
              and the buyer is told nothing after paying. */}
          {/* ⛔ DEVELOPMENT'S HANDLER, INSIDE #441's lazyRoute(). Two independent facts, and
              both are load-bearing. The HANDLER is development's because #441's `goToStore`
              is `window.location.href = "/"` -- a full page reload -- while development's
              navigates IN-ROUTER, which is the version mutation-proved to actually leave the
              page (restore replaceState and the signed-in test fails). The WRAPPER is #441's
              because this Routes block has no Suspense ancestor: OrderComplete is lazy(), so
              development's bare element here would suspend with nothing to catch it and
              white-screen the buyer who just paid. */}
          {/* BETA-COMMERCE-290: with the shop hero retired, "continue" lands on the Vault (the
              authenticated home), not a blank shop view. navigate("/") alone would not re-open it
              (no user transition), so the Vault is opened explicitly -- through the viewport-aware
              opener (V-NAV-VIEWPORT), so a phone buyer lands on the mobile vault, not the overlay. */}
          <Route path="/order-complete" element={lazyRoute(<OrderComplete onContinue={() => { setCurrentView("shop"); navigate("/"); openVault(); }} />)} />
          <Route path="*" element={storeApp} />
        </Routes>
        </GradingFlowProvider>
      </IdentityProvider>
    );
  }
